import { ApiClient } from "./api";
import { DocumentStore } from "./store";
import {
  guardOf,
  GuardError,
  LeaseBusyError,
  differences,
  filesOf,
} from "./database";
import { isFolderMarker } from "./paths";
import { FOLDER_KEY_NAME, isFolderKey } from "./model";
import {
  VaultError,
  sameTarget,
  type Snapshot,
  type CheckpointResult,
  type Attempt,
  type Guard,
} from "./model";
export class VaultSync extends EventTarget {
  status:
    | "idle"
    | "waiting"
    | "refreshing"
    | "checkpointing"
    | "checking"
    | "offline" = "idle";
  error: string | null = null;
  private queue: Promise<unknown> = Promise.resolve();
  private pendingOperations = 0;
  private get busy() {
    return this.pendingOperations > 0;
  }
  private owner = crypto.randomUUID();
  private retryAt = 0;
  private failures = 0;
  constructor(
    readonly store: DocumentStore,
    readonly api: ApiClient,
  ) {
    super();
  }
  private emit() {
    this.dispatchEvent(new Event("change"));
  }
  private waitForLease(expires: number): Promise<void> {
    return new Promise((resolve) => {
      const wake = () => {
        clearTimeout(timer);
        this.store.removeEventListener("change", wake);
        resolve();
      };
      const timer = setTimeout(
        wake,
        Math.max(10, Math.min(1000, expires - Date.now())),
      );
      this.store.addEventListener("change", wake, { once: true });
    });
  }
  private async acquireLease(browserManaged: boolean): Promise<Guard> {
    for (;;) {
      try {
        return await this.store.database.lease(this.owner, browserManaged);
      } catch (error) {
        if (!(error instanceof LeaseBusyError)) throw error;
        // Older clients and browsers without Web Locks still use a fenced lease.
        // Contention is normal: wait for a broadcast or expiry, without an alert.
        await this.waitForLease(error.expires);
      }
    }
  }
  private guarded<T>(run: (fence: number) => Promise<T>): Promise<T> {
    this.pendingOperations++;
    const requested = this.store.database.load().then(
      (state) => ({ epoch: state.vault.epoch }),
      (error: unknown) => ({ error }),
    );
    const operation = this.queue.then(async () => {
      this.error = null;
      this.status = "waiting";
      this.emit();
      const locked = async (browserManaged: boolean) => {
        const guard = await this.acquireLease(browserManaged);
        const renewal = setInterval(() => {
          void this.store.database
            .renewLease(this.owner, guard.fence)
            .catch(() => {});
        }, 30000);
        try {
          const context = await requested;
          if ("error" in context) throw context.error;
          if ((await this.store.database.load()).vault.epoch !== context.epoch)
            throw new GuardError();
          await this.store.flush();
          return await run(guard.fence);
        } finally {
          clearInterval(renewal);
          await this.store.database
            .release(this.owner, guard.fence)
            .catch(() => {});
        }
      };
      try {
        await this.store.flush();
        // Separate from the short database mutation lock: typing stays available.
        // A browser-managed lease can be reclaimed immediately after a tab closes.
        const result =
          typeof navigator !== "undefined" && navigator.locks
            ? await navigator.locks.request(
                `turbonotes2:sync:${this.store.database.vaultId}`,
                () => locked(true),
              )
            : await locked(false);
        this.failures = 0;
        this.retryAt = 0;
        this.status = "idle";
        return result;
      } catch (error) {
        this.failures++;
        this.retryAt =
          Date.now() + Math.min(1800000, 30000 * 2 ** (this.failures - 1));
        this.error =
          error instanceof Error ? error.message : "GitHub sync failed.";
        this.status = "offline";
        throw error;
      } finally {
        this.pendingOperations--;
        await this.store.changed().catch(() => {});
        this.emit();
      }
    });
    // Keep later requests running even if a preceding request failed.
    this.queue = operation.catch(() => {});
    return operation;
  }
  private async current(fence: number, attempt?: Attempt): Promise<Guard> {
    if (this.store.unsaved) await this.store.flush();
    const s = await this.store.database.load();
    if (
      s.vault.fence !== fence ||
      s.vault.lease?.owner !== this.owner ||
      (attempt &&
        (s.vault.epoch !== attempt.epoch ||
          !sameTarget(s.vault.connection, attempt.target)))
    )
      throw new GuardError();
    return guardOf(s);
  }
  private async publish(attempt: Attempt, fence: number): Promise<void> {
    this.status = "checking";
    this.emit();
    let result: CheckpointResult;
    try {
      result = await this.api.request<CheckpointResult>(
        "/checkpoints",
        "POST",
        attempt,
      );
    } catch (e) {
      if (
        e instanceof VaultError &&
        e.code === "checkpoint_unknown" &&
        e.recoveryToken
      ) {
        const token = e.recoveryToken;
        await this.store.database.mutate(
          (s) => {
            const current = s.attempts.find(
              (a) => a.id === attempt.id && a.state === "submitted",
            );
            if (current) current.recoveryToken = token;
          },
          await this.current(fence, attempt),
        );
      }
      if (
        e instanceof VaultError &&
        [
          "stale_base",
          "invalid_request",
          "invalid_vault",
          "vault_too_large",
        ].includes(e.code)
      ) {
        await this.store.database.rejectAttempt(
          attempt.id,
          await this.current(fence, attempt),
        );
      }
      throw e;
    }
    for (let i = 0; i < 5; i++) {
      try {
        await this.store.database.acknowledge(
          result,
          await this.current(fence, attempt),
        );
        return;
      } catch (e) {
        if (!(e instanceof GuardError) || i === 4) throw e;
      }
    }
  }
  async refresh(choice: "merge" | "local" | "remote" = "merge"): Promise<void> {
    return this.guarded(async (fence) => {
      this.status = "refreshing";
      this.emit();
      const s = await this.store.database.load();
      if (!s.vault.connection) return;
      const pending = s.attempts.find((a) => a.state === "submitted");
      if (pending) {
        try {
          await this.publish(pending, fence);
        } catch (e) {
          if (!(e instanceof VaultError) || e.code !== "stale_base") throw e;
        }
      }
      await this.fetchAndApply(fence, choice);
    });
  }
  private async fetchAndApply(
    fence: number,
    choice: "merge" | "local" | "remote" = "merge",
  ): Promise<void> {
    const captured = await this.current(fence);
    const target = (await this.store.database.load()).vault.connection;
    const snapshot = await this.api.request<Snapshot>(
      "/vault/refresh",
      "POST",
      { target },
    );
    for (let i = 0; i < 5; i++) {
      const now = await this.current(fence);
      if (
        now.epoch !== captured.epoch ||
        now.baseCommitSha !== captured.baseCommitSha
      )
        throw new GuardError();
      try {
        // Encrypted notes merge against this generation; a save meanwhile fails the
        // guard and starts the merge again.
        const state = await this.store.database.load();
        if (state.vault.generation !== now.generation) throw new GuardError();
        const encrypted =
          choice === "merge" ? await this.store.encryptedMerges(state, snapshot) : [];
        await this.store.database.applySnapshot(snapshot, now, choice, encrypted);
        break;
      } catch (e) {
        if (!(e instanceof GuardError) || i === 4) throw e;
      }
    }
  }
  async checkpoint(message: string): Promise<void> {
    await this.guarded(async (fence) => {
      await this.checkpointCurrent(message, fence);
    });
  }
  async save(message: string, onlyPaths?: readonly string[]): Promise<void> {
    await this.guarded(async (fence) => {
      const pending = (await this.store.database.load()).attempts.find(
        (a) => a.state === "submitted",
      );
      if (pending) {
        try {
          await this.publish(pending, fence);
        } catch (error) {
          if (!(error instanceof VaultError) || error.code !== "stale_base")
            throw error;
          await this.fetchAndApply(fence);
        }
      }
      // Recover the immutable older attempt first, then capture the requested save.
      await this.checkpointCurrent(message, fence, onlyPaths);
    });
  }
  private async checkpointCurrent(
    message: string,
    fence: number,
    onlyPaths?: readonly string[],
  ): Promise<void> {
    this.status = "checkpointing";
    this.error = null;
    this.emit();
    for (let round = 0; round < 3; round++) {
      const attempt = await this.store.database.capture(
        message,
        await this.current(fence),
        onlyPaths,
      );
      try {
        await this.publish(attempt, fence);
        break;
      } catch (error) {
        if (
          !(error instanceof VaultError) ||
          error.code !== "stale_base" ||
          round === 2
        )
          throw error;
        // The old attempt was conclusively not published. Merge against a
        // complete snapshot, then capture a fresh immutable UUID/payload.
        await this.fetchAndApply(fence);
      }
    }
  }
  async tick(): Promise<void> {
    const s = this.store.state;
    if (
      !s ||
      this.busy ||
      !navigator.onLine ||
      this.store.unsaved ||
      !s.vault.connection ||
      !this.api.session.user ||
      !sameTarget(s.vault.connection, this.api.session.connection) ||
      (!s.vault.baseCommitSha && !s.vault.remoteLoaded) ||
      s.attempts.some((a) => a.state === "submitted") ||
      s.recovery.some((r) => !r.resolved) ||
      Date.now() < this.retryAt
    )
      return;
    if (
      differences(filesOf(s, true), filesOf(s)).length &&
      (s.documents.some((d) => (isFolderMarker(d.path) || isFolderKey(d.path)) && d.dirty) ||
        Date.now() - Math.max(s.vault.lastCheckpoint, s.vault.createdAt) >=
          s.metadata.value.checkpoint.auto_minutes * 60000)
    )
      await this.checkpoint("TurboNotes automatic checkpoint").catch(() => {});
  }
  /** With `key`, creates an encrypted folder whose marker is that folder key. */
  async createFolder(
    path: string,
    key?: string,
  ): Promise<"existing" | "local" | "checkpointed"> {
    const created = await this.store.database.folder(path, key);
    await this.store.changed();
    const marker = `${path}/${key === undefined ? ".gitkeep" : FOLDER_KEY_NAME}`;
    if (
      !created &&
      !this.store.state?.documents.some((d) => d.path === marker && d.dirty)
    )
      return "existing";
    const vault = this.store.state?.vault;
    if (
      !navigator.onLine ||
      !vault?.connection ||
      !this.api.session.user ||
      !sameTarget(vault.connection, this.api.session.connection) ||
      (!vault.baseCommitSha && !vault.remoteLoaded)
    )
      return "local";
    try {
      await this.save(`Create folder ${path}`, [marker]);
      return "checkpointed";
    } catch (error) {
      throw new Error(
        `Folder created locally. GitHub checkpoint pending: ${error instanceof Error ? error.message : "Please retry."}`,
      );
    }
  }
  connectivityChanged(): void {
    this.retryAt = 0;
  }
}
